import React, { useEffect } from "react";
import { SPLASH_MAIN, SPLASH_CENTER } from "@/components/splash/splashPaths";

/*
 * Loader for desktops: the spray-painted smiley drawn live (our own drawing). It gets sprayed
 * on stroke by stroke through a live spray filter, drips, then trembles while the hero photo
 * loads. On .intro-burst it swells and pops into a splash. Timings: App.css ("SMILEY, FULL").
 */

// Paint specks around the ring: fixed pseudo-random so they never jump between visits
const SPECKS = (() => {
  let seed = 7;
  const rand = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };
  return Array.from({ length: 110 }, () => {
    const angle = rand() * Math.PI * 2;
    const dist = 150 + (rand() - 0.35) * 70;
    return {
      cx: 200 + Math.cos(angle) * dist,
      cy: 200 + Math.sin(angle) * dist,
      r: 0.6 + rand() * rand() * 3.2,
      o: 0.45 + rand() * 0.55,
    };
  });
})();

// Paint running down from the ring and the smile: x, start y, width, length, delay (ms)
const DRIPS = [
  { x: 112, y: 286, w: 6, len: 40, delay: 0 },
  { x: 150, y: 324, w: 5, len: 26, delay: 180 },
  { x: 199, y: 346, w: 7, len: 48, delay: 90 },
  { x: 232, y: 290, w: 5, len: 30, delay: 260 },
  { x: 262, y: 333, w: 5, len: 22, delay: 140 },
  { x: 300, y: 300, w: 6, len: 36, delay: 220 },
];

const SmileyLoaderFull = ({ onReady }) => {
  // nothing to load, it starts right away
  useEffect(() => {
    onReady?.();
  }, [onReady]);

  return (
    <div className="sm sm-full absolute inset-0 flex items-center justify-center" aria-hidden="true">
      <div className="absolute inset-0 spray-texture opacity-60" />
      <div className="sm-shake">
        <svg className="sm-face" viewBox="0 0 400 400" overflow="visible">
          <defs>
            {/* rough sprayed edges + a grainy, uneven coat of paint */}
            <filter id="sm-spray" x="-15%" y="-15%" width="130%" height="140%">
              <feTurbulence type="fractalNoise" baseFrequency="0.09" numOctaves="3" seed="4" result="noise" />
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="8" xChannelSelector="R" yChannelSelector="G" result="rough" />
              <feTurbulence type="fractalNoise" baseFrequency="0.7" numOctaves="2" seed="11" result="grain" />
              <feColorMatrix in="grain" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 -1.6 1.9" result="coat" />
              <feComposite in="rough" in2="coat" operator="in" />
            </filter>
          </defs>

          <g className="sm-paint" fill="none" stroke="#ECCE21" strokeLinecap="round" filter="url(#sm-spray)">
            <path className="sm-stroke sm-ring" pathLength="1" strokeWidth="22" d="M200 50 A150 150 0 1 1 196 50.05" />
            <path className="sm-stroke sm-ring-2" pathLength="1" strokeWidth="7" strokeOpacity="0.8" d="M178 60 A141 141 0 1 1 174 60.3" />
            <path className="sm-stroke sm-eye-1" pathLength="1" strokeWidth="30" d="M158 122 L160 186" />
            <path className="sm-stroke sm-eye-2" pathLength="1" strokeWidth="30" d="M242 120 L240 184" />
            <path className="sm-stroke sm-smile" pathLength="1" strokeWidth="26" d="M108 232 Q200 330 292 226" />
            <path className="sm-stroke sm-tick-1" pathLength="1" strokeWidth="13" d="M93 220 L124 248" />
            <path className="sm-stroke sm-tick-2" pathLength="1" strokeWidth="13" d="M277 214 L307 236" />
            <g stroke="none" fill="#ECCE21">
              {DRIPS.map((drip, i) => (
                <g key={i} className="sm-drip" style={{ "--d": `${drip.delay}ms`, "--len": `${drip.len}px` }}>
                  <rect className="sm-drip-stem" x={drip.x - drip.w / 2} y={drip.y} width={drip.w} height={drip.len} />
                  <circle className="sm-drip-bulb" cx={drip.x} cy={drip.y} r={drip.w * 0.7} />
                </g>
              ))}
            </g>
          </g>

          <g className="sm-specks" fill="#ECCE21">
            {SPECKS.map((s, i) => (
              <circle key={i} cx={s.cx} cy={s.cy} r={s.r} opacity={s.o} />
            ))}
          </g>

          {/* when it pops: sauce bursts out of the face */}
          <g transform="translate(200 210)">
            <g className="sm-burst">
              <path d={SPLASH_MAIN} transform={`translate(${-SPLASH_CENTER.x} ${-SPLASH_CENTER.y})`} fill="#ECCE21" />
            </g>
          </g>
        </svg>
      </div>
    </div>
  );
};

export default SmileyLoaderFull;
